import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiAlertTriangle, FiTrash2 } from 'react-icons/fi';
import { IoFilter } from 'react-icons/io5';
import { MdArrowBackIosNew } from 'react-icons/md';

import NavBar from '../NavBar';
import Cookies from 'js-cookie';
import {
	requestAtualizarSetorEscala,
	requestCatalogosEscalaAdmin,
	requestCriarSetorEscala,
	requestListarSetoresEscala,
	requestNewUnits,
} from '../../services/escalaRequests';
import {
	compareSetoresPorAndar,
	formatAndarEscala,
	sortSetoresPorAndar,
} from '../../utils/escalaSetorOrdering';

const ESCALA_FILTERS_CACHE_KEY = 'escala.filters.cache';

const getDefaultFormCreate = () => ({
	nomeSetorId: '',
	andar: '',
	tipoQuantidade: '',
	quantidade: '',
});

const getDefaultHeaderFilters = () => ({
	setorNome: [],
	andar: [],
	tipoQuantidade: [],
	quantidade: [],
});

const isSetorAtivo = (value) => {
	if (value === false || value === 0 || value === null) return false;
	return !['false', '0', 'inativo', 'inactive'].includes(
		String(value ?? 'true').trim().toLowerCase(),
	);
};

const getUniqueApiErrorMessage = (value, fallback) => {
	const messages = [];
	const visit = (item) => {
		if (typeof item === 'string' || typeof item === 'number') {
			const message = String(item).trim();
			if (message) messages.push(message);
			return;
		}
		if (Array.isArray(item)) {
			item.forEach(visit);
			return;
		}
		if (item && typeof item === 'object') {
			Object.values(item).forEach(visit);
		}
	};

	visit(value);
	const uniqueMessages = Array.from(
		new Map(
			messages.map((message) => [message.trim().toLowerCase(), message]),
		).values(),
	);

	return uniqueMessages.join(' ') || fallback;
};

function ScaleManager() {
	const [token, setToken] = useState('');
	const [estabelecimento, setEstabelecimento] = useState('');
	const [cnesNaoIdentificado, setCnesNaoIdentificado] = useState(false);
	const [showUnidadeFilter, setShowUnidadeFilter] = useState(false);
	const [unidadeOptions, setUnidadeOptions] = useState([]);
	const [unidadeSearchTerm, setUnidadeSearchTerm] = useState('');
	const [showUnidadeSuggestions, setShowUnidadeSuggestions] = useState(false);
	const [setorSearchTerm, setSetorSearchTerm] = useState('');
	const [showSetorSuggestions, setShowSetorSuggestions] = useState(false);
	const [setores, setSetores] = useState([]);
	const [catalogosAdmin, setCatalogosAdmin] = useState({
		tiposSecao: [],
		nomesSetor: [],
		setores: [],
	});
	const [formCreate, setFormCreate] = useState(getDefaultFormCreate);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [formFeedback, setFormFeedback] = useState({ type: '', message: '' });
	const [headerFilters, setHeaderFilters] = useState(getDefaultHeaderFilters);
	const [openHeaderFilter, setOpenHeaderFilter] = useState('');
	const [deleteTarget, setDeleteTarget] = useState(null);
	const [isDeletingItem, setIsDeletingItem] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [accessChecked, setAccessChecked] = useState(false);
	const [hasManagementAccess, setHasManagementAccess] = useState(false);

	const normalizeUnits = (unitsPayload) => {
		const unitsList = Array.isArray(unitsPayload)
			? unitsPayload
			: Array.isArray(unitsPayload?.results)
				? unitsPayload.results
				: [];

		const dedupByCnes = new Map();
		const normalizeUnitName = (value = '') =>
			String(value)
				.normalize('NFD')
				.replace(/[\u0300-\u036f]/g, '')
				.toLowerCase()
				.trim();
		const shouldHideMedicalScaleUnit = (nome = '') =>
			normalizeUnitName(nome) === 'cer andarai';

		unitsList.forEach((item) => {
			const cnes = String(
				item?.cnes ||
					item?.cnes_estabelecimento ||
					item?.codigo_cnes ||
					item?.id ||
					'',
			).trim();
			const nome = String(
				item?.nome || item?.unidade || item?.nome_fantasia || '',
			).trim();

			if (
				!cnes ||
				!nome ||
				shouldHideMedicalScaleUnit(nome) ||
				dedupByCnes.has(cnes)
			)
				return;
			dedupByCnes.set(cnes, { cnes, nome });
		});

		return Array.from(dedupByCnes.values());
	};

	const readCachedUnidade = () => {
		try {
			const raw = localStorage.getItem(ESCALA_FILTERS_CACHE_KEY);
			if (!raw) return '';
			const parsed = JSON.parse(raw);
			return String(parsed?.unidade || '').trim();
		} catch (_error) {
			return '';
		}
	};

	const persistCachedUnidade = (unidade) => {
		try {
			const raw = localStorage.getItem(ESCALA_FILTERS_CACHE_KEY);
			const parsed = raw ? JSON.parse(raw) : {};
			localStorage.setItem(
				ESCALA_FILTERS_CACHE_KEY,
				JSON.stringify({ ...parsed, unidade: unidade || '' }),
			);
		} catch (_error) {
			// no-op
		}
	};

	const normalizeUnitSearch = useCallback(
		(value = '') =>
			String(value)
				.normalize('NFD')
				.replace(/[\u0300-\u036f]/g, '')
				.toLowerCase()
				.trim(),
		[],
	);

	const formatUnidadeOptionLabel = useCallback(
		(unit) => {
			const nome = String(unit?.nome || '').trim();
			return normalizeUnitSearch(nome) ===
				normalizeUnitSearch('CER Jacarepaguá')
				? 'Hospital Cardoso Fontes'
				: nome;
		},
		[normalizeUnitSearch],
	);

	const formatSetorOptionLabel = useCallback(
		(setor) => String(setor?.nome || '').trim(),
		[],
	);

	const mapSetor = useCallback(
		(item) => ({
			id: item?.id,
			nome: item?.nome || item?.nome_exibicao || '-',
			nomeExibicao: item?.nome_exibicao || item?.nome || '-',
			nomeSetorId: String(item?.nome_setor?.id || ''),
			andar: item?.andar || '-',
			tipoCapacidade: String(item?.tipo_capacidade || '').toUpperCase(),
			tipoCapacidadeDisplay:
				item?.tipo_capacidade_display ||
				(String(item?.tipo_capacidade || '').toUpperCase() === 'SALA'
					? 'Sala'
					: 'Leito'),
			quantidadeCapacidade: Number(item?.quantidade_capacidade ?? 0),
			ativo: isSetorAtivo(item?.ativo),
		}),
		[],
	);

	const loadManagerDataByUnit = useCallback(
		async (authToken, cnes) => {
			if (!authToken || !cnes) {
				setSetores([]);
				return;
			}

			setIsLoading(true);

			try {
				const setoresResponse = await requestListarSetoresEscala({
					token: authToken,
					estabelecimento: cnes,
					incluirInativos: true,
				});

				if (setoresResponse?.error) {
					setSetores([]);
					return;
				}

				const todosSetores = sortSetoresPorAndar(
					(setoresResponse || []).map(mapSetor),
				);
				setSetores(todosSetores);

				const catalogos = await requestCatalogosEscalaAdmin({
					token: authToken,
					estabelecimento: cnes,
					incluirInativos: false,
				});
				if (!catalogos?.error) {
					setCatalogosAdmin(catalogos);
				}
			} finally {
				setIsLoading(false);
			}
		},
		[mapSetor],
	);

	useEffect(() => {
		const localData = Cookies.get('user');
		if (!localData) {
			setHasManagementAccess(false);
			setAccessChecked(true);
			return;
		}

		try {
			const parsed = JSON.parse(localData);
			const normalizeProfileValue = (value = '') =>
				String(value || '')
					.normalize('NFD')
					.replace(/[\u0300-\u036f]/g, '')
					.toLowerCase()
					.trim();
			const authToken = parsed.token || '';
			const cargoNormalizado = normalizeProfileValue(parsed.cargo);
			const usuarioNormalizado = normalizeProfileValue(
				parsed.username || parsed.nome || '',
			);
			const unidadeNormalizada = normalizeProfileValue(parsed.unidade);
			const permissoes = Array.isArray(parsed?.permissao)
				? parsed.permissao
				: [parsed?.permissao].filter(Boolean);
			const hasDeveloperPermission = permissoes.some(
				(permissao) =>
					normalizeProfileValue(permissao) === 'desenvolvedor',
			);
			const possuiAcessoGerenciamento =
				cargoNormalizado === 'desenvolvedor' ||
				hasDeveloperPermission ||
				cargoNormalizado === 'subhue' ||
				usuarioNormalizado.includes('subhue') ||
				unidadeNormalizada === 'subhue' ||
				permissoes.some((permissao) =>
					normalizeProfileValue(permissao).includes('subhue'),
				);

			setHasManagementAccess(possuiAcessoGerenciamento);
			setAccessChecked(true);

			if (!possuiAcessoGerenciamento) return;

			const buscaGeralCatalogos =
				cargoNormalizado === 'desenvolvedor' ||
				cargoNormalizado === 'subhue' ||
				usuarioNormalizado.includes('subhue') ||
				unidadeNormalizada === 'subhue' ||
				unidadeNormalizada.length === 0;

			const cnes = String(
				parsed.cnes ||
					parsed.cnes_estabelecimento ||
					parsed.cnesEstabelecimento ||
					'',
			).trim();
			const cnesCache = readCachedUnidade();
			const cnesInicial = buscaGeralCatalogos ? cnesCache : cnes;

			setToken(authToken);
			setShowUnidadeFilter(buscaGeralCatalogos);
			setEstabelecimento(cnesInicial);
			setCnesNaoIdentificado(!buscaGeralCatalogos && !cnes);

			if (buscaGeralCatalogos) {
				(async () => {
					const unidades = await requestNewUnits();
					if (!unidades?.error) {
						setUnidadeOptions(normalizeUnits(unidades));
					}
				})();
			}
		} catch (_error) {
			setHasManagementAccess(false);
			setAccessChecked(true);
		}
	}, []);

	useEffect(() => {
		if (!hasManagementAccess) return;
		if (!token) return;

		if (showUnidadeFilter) {
			persistCachedUnidade(estabelecimento);
		}

		if (!estabelecimento) {
			setSetores([]);
			return;
		}

		loadManagerDataByUnit(token, estabelecimento);
	}, [
		hasManagementAccess,
		token,
		showUnidadeFilter,
		estabelecimento,
		loadManagerDataByUnit,
	]);

	const operacoesBloqueadas =
		!hasManagementAccess ||
		!token ||
		(showUnidadeFilter
			? !estabelecimento
			: cnesNaoIdentificado || !estabelecimento);

	const unidadeOptionsFiltradas = useMemo(() => {
		const termo = normalizeUnitSearch(unidadeSearchTerm);
		const unidadesVisiveis = unidadeOptions.filter(
			(unit) => normalizeUnitSearch(unit?.nome || '') !== 'cer andarai',
		);
		if (!termo) return unidadesVisiveis;

		return unidadesVisiveis.filter((unit) => {
			const nome = normalizeUnitSearch(formatUnidadeOptionLabel(unit));
			const cnes = normalizeUnitSearch(unit?.cnes || '');
			return nome.includes(termo) || cnes.includes(termo);
		});
	}, [
		unidadeOptions,
		unidadeSearchTerm,
		normalizeUnitSearch,
		formatUnidadeOptionLabel,
	]);

	const unidadeSelecionada = useMemo(
		() =>
			unidadeOptions.find(
				(unit) => String(unit.cnes) === String(estabelecimento),
			) || null,
		[unidadeOptions, estabelecimento],
	);

	const unidadeInputInvalida =
		showUnidadeFilter &&
		String(unidadeSearchTerm || '').trim() !== '' &&
		!unidadeSelecionada;

	const handleUnidadeInputChange = (value) => {
		setUnidadeSearchTerm(value);
		setShowUnidadeSuggestions(true);

		const inputNormalizado = normalizeUnitSearch(value);

		const unidadeCorrespondente = unidadeOptions.find((unit) => {
			const label = normalizeUnitSearch(formatUnidadeOptionLabel(unit));
			const cnes = normalizeUnitSearch(unit?.cnes || '');
			return label === inputNormalizado || cnes === inputNormalizado;
		});

		setEstabelecimento(
			unidadeCorrespondente ? String(unidadeCorrespondente.cnes) : '',
		);
	};

	const handleSelectUnidade = (unit) => {
		setUnidadeSearchTerm(formatUnidadeOptionLabel(unit));
		setEstabelecimento(String(unit.cnes));
		setShowUnidadeSuggestions(false);
	};

	useEffect(() => {
		if (!showUnidadeFilter || !estabelecimento) return;

		const unidadeAtual = unidadeOptions.find(
			(unit) => String(unit.cnes) === String(estabelecimento),
		);
		if (!unidadeAtual) return;

		const displayValue = formatUnidadeOptionLabel(unidadeAtual);
		if (
			normalizeUnitSearch(unidadeSearchTerm) !==
			normalizeUnitSearch(displayValue)
		) {
			setUnidadeSearchTerm(displayValue);
		}
	}, [
		showUnidadeFilter,
		estabelecimento,
		unidadeOptions,
		unidadeSearchTerm,
		formatUnidadeOptionLabel,
		normalizeUnitSearch,
	]);

	const setorOptionsForm = useMemo(() => {
		const mapa = new Map();

		(catalogosAdmin.nomesSetor || []).forEach((setor) => {
			const id = String(setor?.id || '').trim();
			const nome = String(setor?.nome || '-').trim();
			const ativo = setor?.ativo !== false;

			if (!ativo || !id || !nome) return;
			if (mapa.has(id)) return;

			mapa.set(id, {
				id,
				nome,
				ativo,
			});
		});

		return sortSetoresPorAndar(Array.from(mapa.values()));
	}, [catalogosAdmin.nomesSetor]);

	const setorOptionsFiltradas = useMemo(() => {
		const termo = normalizeUnitSearch(setorSearchTerm);
		if (!termo) return setorOptionsForm;

		return setorOptionsForm.filter((setor) => {
			const nome = normalizeUnitSearch(setor?.nome || '');
			return nome.includes(termo);
		});
	}, [setorOptionsForm, setorSearchTerm, normalizeUnitSearch]);

	const setorSelecionado = useMemo(
		() =>
			setorOptionsForm.find(
				(setor) => String(setor.id) === String(formCreate.nomeSetorId),
			) || null,
		[setorOptionsForm, formCreate.nomeSetorId],
	);

	const setorInputInvalido =
		String(setorSearchTerm || '').trim() !== '' && !setorSelecionado;

	const andarOptions = [
		{ value: '-1', label: 'Subsolo' },
		...Array.from({ length: 10 }, (_, i) => ({
			value: String(i + 1),
			label: `${i + 1}`,
		})),
	];

	const rows = useMemo(() => {
		return setores
			.filter((setor) => setor.ativo)
			.map((setor) => ({
				id: `setor-${setor.id}`,
				setorId: setor.id,
				setorNome: setor.nomeExibicao,
				andar: setor.andar || '-',
				tipoQuantidade: setor.tipoCapacidadeDisplay,
				quantidade: setor.quantidadeCapacidade,
				deleteType: 'setor',
				deleteId: setor.id,
				deleteNome: setor.nomeExibicao,
			}))
			.sort(compareSetoresPorAndar);
	}, [setores]);

	const headerFilterOptions = useMemo(() => {
		const buildOptions = (
			items,
			getValue,
			getLabel = getValue,
			sortOptions = true,
		) => {
			const mapa = new Map();
			items.forEach((item) => {
				const value = String(getValue(item) ?? '').trim();
				if (!value || mapa.has(value)) return;
				mapa.set(value, {
					value,
					label: String(getLabel(item) ?? value),
				});
			});
			const options = Array.from(mapa.values());
			return sortOptions
				? options.sort((a, b) =>
						a.label.localeCompare(b.label, 'pt-BR'),
					)
				: options;
		};

		const andaresDisponiveis = rows.map((item) => item.andar);
		const andar = buildOptions(
			rows,
			(item) => item.andar,
			(item) => formatAndarEscala(item.andar, andaresDisponiveis),
		).sort((a, b) =>
			compareSetoresPorAndar(
				{ andar: a.value, nome: a.label },
				{ andar: b.value, nome: b.label },
			),
		);

		return {
			setorNome: buildOptions(
				rows,
				(item) => item.setorNome,
				(item) => item.setorNome,
				false,
			),
			andar,
			tipoQuantidade: buildOptions(rows, (item) => item.tipoQuantidade),
			quantidade: buildOptions(
				rows,
				(item) => String(item.quantidade || 0),
				(item) => String(item.quantidade || 0),
			),
		};
	}, [rows]);

	const filteredRows = useMemo(() => {
		return rows.filter((row) => {
			if (
				headerFilters.setorNome.length > 0 &&
				!headerFilters.setorNome.includes(String(row.setorNome))
			) {
				return false;
			}

			if (
				headerFilters.andar.length > 0 &&
				!headerFilters.andar.includes(String(row.andar))
			) {
				return false;
			}

			if (
				headerFilters.tipoQuantidade.length > 0 &&
				!headerFilters.tipoQuantidade.includes(
					String(row.tipoQuantidade),
				)
			) {
				return false;
			}

			if (
				headerFilters.quantidade.length > 0 &&
				!headerFilters.quantidade.includes(String(row.quantidade || 0))
			) {
				return false;
			}

			return true;
		});
	}, [rows, headerFilters]);

	const handleFormChange = (field, value) => {
		setFormCreate((prev) => {
			if (field === 'nomeSetorId') {
				return {
					...prev,
					nomeSetorId: value,
				};
			}
			return { ...prev, [field]: value };
		});
		setFormFeedback({ type: '', message: '' });
	};

	const handleSetorInputChange = (value) => {
		setSetorSearchTerm(value);
		setShowSetorSuggestions(true);

		const inputNormalizado = normalizeUnitSearch(value);
		const setorCorrespondente = setorOptionsForm.find((setor) => {
			const nome = normalizeUnitSearch(formatSetorOptionLabel(setor));
			return nome === inputNormalizado;
		});

		handleFormChange(
			'nomeSetorId',
			setorCorrespondente ? String(setorCorrespondente.id) : '',
		);
	};

	const handleSelectSetor = (setor) => {
		setSetorSearchTerm(formatSetorOptionLabel(setor));
		handleFormChange('nomeSetorId', String(setor.id));
		setShowSetorSuggestions(false);
	};

	useEffect(() => {
		const setorAtual = setorOptionsForm.find(
			(setor) => String(setor.id) === String(formCreate.nomeSetorId),
		);

		if (!setorAtual) {
			if (formCreate.nomeSetorId) {
				setFormCreate((prev) => ({ ...prev, nomeSetorId: '' }));
			}
			return;
		}

		const displayValue = formatSetorOptionLabel(setorAtual);
		if (
			normalizeUnitSearch(setorSearchTerm) !==
			normalizeUnitSearch(displayValue)
		) {
			setSetorSearchTerm(displayValue);
		}
	}, [
		setorOptionsForm,
		formCreate.nomeSetorId,
		setorSearchTerm,
		formatSetorOptionLabel,
		normalizeUnitSearch,
	]);

	const handleCriarSetor = async () => {
		if (operacoesBloqueadas || isSubmitting) return;

		const { nomeSetorId, andar, tipoQuantidade, quantidade } = formCreate;
		if (!nomeSetorId || !andar || !tipoQuantidade || !quantidade) {
			setFormFeedback({
				type: 'error',
				message: 'Preencha todos os campos antes de enviar.',
			});
			return;
		}

		setIsSubmitting(true);
		try {
			const payloadBase = {
				estabelecimento,
				nome_setor_id: String(nomeSetorId),
				andar: Number(andar),
				tipo_capacidade:
					String(tipoQuantidade || '').toUpperCase() === 'SALA'
						? 'SALA'
						: 'LEITO',
				quantidade_capacidade: Number(quantidade),
				ativo: true,
			};

			const result = await requestCriarSetorEscala({
				token,
				body: payloadBase,
			});

			const mensagemSucesso = 'Registro salvo com sucesso!';

			if (result?.error) {
				setFormFeedback({
					type: 'error',
					message: getUniqueApiErrorMessage(
						result.error?.detail ||
							result.error?.mensagem ||
							result.error?.data ||
							result.error,
						'Erro ao salvar registro. Tente novamente.',
					),
				});
				return;
			}

			setFormFeedback({
				type: 'success',
				message: mensagemSucesso,
			});
			setFormCreate(getDefaultFormCreate());
			setSetorSearchTerm('');
			setShowSetorSuggestions(false);
			await loadManagerDataByUnit(token, estabelecimento);
		} finally {
			setIsSubmitting(false);
		}
	};

	const toggleHeaderFilterOption = (filterKey, value) => {
		setHeaderFilters((prev) => {
			const currentValues = prev[filterKey] || [];
			const normalizedValue = String(value);
			const exists = currentValues.includes(normalizedValue);

			return {
				...prev,
				[filterKey]: exists
					? currentValues.filter((item) => item !== normalizedValue)
					: [...currentValues, normalizedValue],
			};
		});
	};

	const hasHeaderFilterSelected = (filterKey) =>
		(headerFilters[filterKey] || []).length > 0;

	const renderHeaderFilter = (
		filterKey,
		title,
		options,
		align = 'center',
	) => (
		<div
			className={`relative px-4 py-3 ${align === 'left' ? 'text-left' : 'text-center'}`}>
			<div
				className={`flex items-center gap-2 ${align === 'left' ? 'justify-start' : 'justify-center'}`}>
				<span>{title}</span>
				<button
					type='button'
					onClick={() =>
						setOpenHeaderFilter((prev) =>
							prev === filterKey ? '' : filterKey,
						)
					}
					className={`transition-colors ${hasHeaderFilterSelected(filterKey) ? 'text-[#BFD4EE]' : 'text-white/85 hover:text-white'}`}>
					<IoFilter size={15} />
				</button>
			</div>

			{openHeaderFilter === filterKey && (
				<div
					className={`absolute top-[46px] z-30 w-[220px]  rounded-[8px] border border-[#D8DCE2] bg-white p-3 text-left shadow-lg ${align === 'left' ? 'left-2' : 'left-1/2 -translate-x-1/2'}`}>
					<div className='max-h-[220px] overflow-y-auto pr-1'>
						{options.length > 0 ? (
							options.map((option) => {
								const checked = (
									headerFilters[filterKey] || []
								).includes(option.value);

								return (
									<label
										key={option.value}
										className='flex items-center gap-2 py-1.5 text-[13px] text-[#4B5563] cursor-pointer'>
										<input
											type='checkbox'
											checked={checked}
											onChange={() =>
												toggleHeaderFilterOption(
													filterKey,
													option.value,
												)
											}
											className='h-4 w-4 rounded border-[#CBD5E1] text-[#17395f] focus:ring-[#9EC3E7]'
										/>
										<span>{option.label}</span>
									</label>
								);
							})
						) : (
							<div className='py-2 text-[12px] text-[#9CA3AF]'>
								Nenhuma opção disponível
							</div>
						)}
					</div>

					{hasHeaderFilterSelected(filterKey) && (
						<button
							type='button'
							onClick={() =>
								setHeaderFilters((prev) => ({
									...prev,
									[filterKey]: [],
								}))
							}
							className='mt-2 text-[12px] font-medium text-[#17395f] hover:underline'>
							Limpar filtro
						</button>
					)}
				</div>
			)}
		</div>
	);

	const abrirPopupExclusao = (row) => {
		if (operacoesBloqueadas) return;
		setDeleteTarget({
			id: row.deleteId,
			tipo: row.deleteType,
			nome: row.deleteNome,
		});
	};

	const fecharPopupExclusao = () => {
		if (isDeletingItem) return;
		setDeleteTarget(null);
	};

	const confirmarExclusaoItem = async () => {
		if (!deleteTarget || isDeletingItem || operacoesBloqueadas) return;

		setIsDeletingItem(true);

		try {
			await requestAtualizarSetorEscala({
				token,
				id: deleteTarget.id,
				body: { ativo: false },
			});

			await loadManagerDataByUnit(token, estabelecimento);
			setDeleteTarget(null);
		} finally {
			setIsDeletingItem(false);
		}
	};

	const getDeleteTargetLabel = () => 'o registro';

	if (!accessChecked) {
		return (
			<div className='min-h-screen flex flex-col'>
				<NavBar />
				<div className='flex flex-1 items-center justify-center bg-[#F0F4F8]'>
					<div className='w-8 h-8 rounded-full border-4 border-[#dbeafe] border-t-[#2563eb] animate-spin' />
				</div>
			</div>
		);
	}

	if (!hasManagementAccess) {
		return (
			<div className='min-h-screen flex flex-col'>
				<NavBar />
				<div className='font-inter flex-1'>
					<section className='w-full bg-[#E9EDF2] border-[#d9dde3] mt-[80px]'>
						<div className='max-w-[1500px] mx-auto w-[95%] py-6'>
							<h1 className='text-center text-[#13335A] text-[26px] sm:text-[28px] font-semibold'>
								Gerenciamento de Escala
							</h1>
						</div>
					</section>

					<main className='flex-1 max-w-[900px] mx-auto w-[95%] py-10'>
						<Link
							to='/escala'
							className='inline-flex items-center gap-1.5 text-[18px] font-medium text-[#17395f] hover:text-[#60758f] duration-300 transition-colors mb-4'>
							<MdArrowBackIosNew size={15} />
							Voltar
						</Link>

						<div className='rounded-[8px] border border-[#f1b3b3] bg-[#fff1f1] px-5 py-4 text-[#9b1c1c] shadow-sm'>
							<div className='flex items-start gap-3'>
								<FiAlertTriangle
									size={22}
									className='mt-0.5 shrink-0'
								/>
								<div>
									<p className='text-[16px] font-semibold'>
										Acesso negado
									</p>
									<p className='mt-1 text-[14px] leading-6'>
										Esta pagina esta disponivel apenas para
										usuarios com perfil desenvolvedor ou
										Subhue.
									</p>
								</div>
							</div>
						</div>
					</main>
				</div>
			</div>
		);
	}

	return (
		<div className='min-h-screen  flex flex-col'>
			<NavBar />
			<div className='font-inter'>
				<section className='w-full bg-[#E9EDF2] border-[#d9dde3] mt-[80px]'>
					<div className='max-w-[1500px] mx-auto w-[95%] py-6'>
						<h1 className='text-center text-[#13335A] text-[26px] sm:text-[28px] font-semibold'>
							Gerenciamento de Escala
						</h1>
					</div>
				</section>

				<main className='flex-1 max-w-[1500px] mx-auto w-[95%] py-10'>
					<Link
						to='/escala'
						className='inline-flex items-center  gap-1.5 text-[18px]  font-medium text-[#17395f] hover:text-[#60758f] duration-300 transition-colors mb-4'>
						<MdArrowBackIosNew size={15} />
						Voltar
					</Link>
					{/* <h2 className='text-[#17395f] text-[22px] font-semibold mb-6'>
						Gerencie sua Unidade
					</h2> */}

					{showUnidadeFilter && (
						<div className='mb-4  rounded-[8px] border border-[#dde2e8] bg-white px-4 py-4 relative shadow-sm'>
							<p className='text-[13px] text-[#4B5563] mb-2 font-medium'>
								Unidade
							</p>
							<input
								id='manager-unidade'
								type='text'
								value={unidadeSearchTerm}
								onChange={(e) =>
									handleUnidadeInputChange(e.target.value)
								}
								onFocus={() => setShowUnidadeSuggestions(true)}
								onBlur={() => {
									setTimeout(
										() => setShowUnidadeSuggestions(false),
										120,
									);
								}}
								placeholder='Digite para buscar e selecionar unidade'
								className={`w-full max-w-[420px] border  rounded-[8px] px-3 py-2 text-[14px] text-[#4B5563] focus:outline-none focus:ring-2 focus:ring-[#9EC3E7] ${
									unidadeInputInvalida
										? 'border-[#dc2626]'
										: 'border-[#D8DCE2]'
								}`}
							/>
							{showUnidadeSuggestions && (
								<div className='absolute left-4 right-4 top-[82px] z-30 max-w-[420px] max-h-[220px] overflow-y-auto  rounded-[8px] border border-[#D8DCE2] bg-white shadow-sm'>
									{unidadeOptionsFiltradas.length > 0 ? (
										unidadeOptionsFiltradas.map((unit) => (
											<button
												type='button'
												key={unit.cnes}
												onMouseDown={(e) => {
													e.preventDefault();
													handleSelectUnidade(unit);
												}}
												className='block w-full px-3 py-2 text-left text-[13px] text-[#4B5563] hover:bg-[#F3F4F6]'>
												{formatUnidadeOptionLabel(unit)}
											</button>
										))
									) : (
										<div className='px-3 py-2 text-[12px] text-[#9CA3AF]'>
											Nenhuma unidade encontrada
										</div>
									)}
								</div>
							)}
						</div>
					)}

					{cnesNaoIdentificado && (
						<div className='mb-4 rounded-[8px] border border-[#f1b3b3] bg-[#fff1f1] px-4 py-3 text-[13px] text-[#9b1c1c]'>
							Não foi possível identificar o CNES do
							estabelecimento do usuário.
						</div>
					)}

					{showUnidadeFilter && !estabelecimento && (
						<div className='mb-4 rounded-[8px] border border-[#f1d59b] bg-[#fff8e8] px-4 py-3 text-[13px] text-[#8a5a00]'>
							Selecione uma unidade para carregar o gerenciamento.
						</div>
					)}

					<div className='rounded-[8px] border border-[#dde2e8] bg-white px-4 py-4 shadow-sm'>
						<div className='grid grid-cols-1 md:grid-cols-[1.5fr_0.9fr_0.9fr_0.9fr_auto] gap-3 items-end'>
							<div className='relative'>
								<label className='text-[14px] text-[#585d63] block mb-2'>
									Setor
								</label>
								<input
									type='text'
									value={setorSearchTerm}
									onChange={(e) =>
										handleSetorInputChange(e.target.value)
									}
									onFocus={() =>
										setShowSetorSuggestions(true)
									}
									onBlur={() => {
										setTimeout(
											() =>
												setShowSetorSuggestions(false),
											120,
										);
									}}
									placeholder='Digite para buscar e selecionar setor'
									className={`w-full h-[44px] rounded-[8px] border px-3 text-[15px] text-[#585d63] outline-none ${
										setorInputInvalido
											? 'border-[#dc2626]'
											: 'border-[#D8DCE2]'
									}`}
								/>
								{showSetorSuggestions && (
									<div className='absolute left-0 right-0 top-[82px] z-30 max-h-[220px] overflow-y-auto rounded-[8px] border border-[#D8DCE2] bg-white shadow-sm'>
										{setorOptionsFiltradas.length > 0 ? (
											setorOptionsFiltradas.map(
												(item) => (
													<button
														type='button'
														key={item.id}
														onMouseDown={(e) => {
															e.preventDefault();
															handleSelectSetor(
																item,
															);
														}}
														className='block w-full px-3 py-2 text-left text-[13px] text-[#4B5563] hover:bg-[#F3F4F6]'>
														{formatSetorOptionLabel(
															item,
														)}
													</button>
												),
											)
										) : (
											<div className='px-3 py-2 text-[12px] text-[#9CA3AF]'>
												Nenhum setor encontrado
											</div>
										)}
									</div>
								)}
							</div>

							<div>
								<label className='text-[14px] text-[#585d63] block mb-2'>
									Andar
								</label>
								<select
									value={formCreate.andar}
									onChange={(e) =>
										handleFormChange(
											'andar',
											e.target.value,
										)
									}
									className='w-full h-[44px] rounded-[8px] border border-[#D8DCE2] px-3 text-[15px] text-[#585d63] outline-none'>
									<option value=''>Andar</option>
									{andarOptions.map((item) => (
										<option
											key={item.value}
											value={item.value}>
											{item.label}
										</option>
									))}
								</select>
							</div>

							<div>
								<label className='text-[14px] text-[#585d63] block mb-2'>
									Sala ou Leito
								</label>
								<select
									value={formCreate.tipoQuantidade}
									onChange={(e) =>
										handleFormChange(
											'tipoQuantidade',
											e.target.value,
										)
									}
									className='w-full h-[44px] rounded-[8px] border border-[#D8DCE2] px-3 text-[15px] text-[#585d63] outline-none'>
									<option value=''>Selecione</option>
									<option value='sala'>Sala</option>
									<option value='leito'>Leito</option>
								</select>
							</div>

							<div>
								<label className='text-[14px] text-[#585d63] block mb-2'>
									Quantidade
								</label>
								<input
									type='text'
									inputMode='numeric'
									value={formCreate.quantidade}
									onKeyDown={(e) => {
										if (
											!/^[0-9]$/.test(e.key) &&
											![
												'Backspace',
												'Delete',
												'ArrowLeft',
												'ArrowRight',
												'Tab',
											].includes(e.key)
										) {
											e.preventDefault();
										}
									}}
									onChange={(e) => {
										const raw = e.target.value.replace(
											/\D/g,
											'',
										);
										const num =
											raw === ''
												? ''
												: Math.min(
														70,
														Math.max(
															1,
															Number(raw),
														),
													);
										handleFormChange(
											'quantidade',
											num === '' ? '' : String(num),
										);
									}}
									placeholder='Somente números'
									className='w-full h-[44px] rounded-[8px] border border-[#D8DCE2] px-3 text-[15px] text-[#585d63] outline-none'
								/>
							</div>

							<button
								type='button'
								onClick={handleCriarSetor}
								disabled={operacoesBloqueadas || isSubmitting}
								className={`h-[44px] rounded-[8px] px-5 text-[16px] font-semibold text-white transition-colors ${
									operacoesBloqueadas || isSubmitting
										? 'bg-[#9fb0c3] cursor-not-allowed'
										: 'bg-[#17395f] hover:bg-[#123252]'
								}`}>
								{isSubmitting ? 'Salvando...' : 'Enviar'}
							</button>
						</div>

						{formFeedback.message &&
							formFeedback.type === 'error' && (
								<div
									className={`mt-3 rounded-[6px] px-3 py-2 text-[13px] ${
										formFeedback.type === 'success'
											? 'bg-[#f0fdf4] border border-[#bbf7d0] text-[#15803d]'
											: 'bg-[#fff1f1] border border-[#f1b3b3] text-[#9b1c1c]'
									}`}>
									{formFeedback.message}
								</div>
							)}
					</div>

					<div className='mt-4 rounded-[8px] border border-[#D9DEE6] bg-white shadow-sm'>
						<div className='w-full overflow-x-auto overflow-y-visible'>
							<div className='min-w-[1000px]'>
								<div className='grid rounded-t-[8px] grid-cols-[1.5fr_0.9fr_1fr_1fr_48px] bg-[#17395f] text-white text-[14px] font-medium'>
									{renderHeaderFilter(
										'setorNome',
										'Setor',
										headerFilterOptions.setorNome,
										'left',
									)}
									{renderHeaderFilter(
										'andar',
										'Andar',
										headerFilterOptions.andar,
									)}
									{renderHeaderFilter(
										'tipoQuantidade',
										'Sala/Leito',
										headerFilterOptions.tipoQuantidade,
									)}
									{renderHeaderFilter(
										'quantidade',
										'Quantidade',
										headerFilterOptions.quantidade,
									)}
									<div className='px-4 py-3' />
								</div>

								{isLoading ? (
									<div className='px-4 py-10 text-center text-[#6B7280] text-[14px]'>
										Carregando dados...
									</div>
								) : filteredRows.length === 0 ? (
									<div className='px-4 py-10 text-center text-[#6B7280] text-[14px]'>
										Nenhum registro encontrado.
									</div>
								) : (
									filteredRows.map((row, index) => (
										<div
											key={row.id}
											className={`grid grid-cols-[1.5fr_0.9fr_1fr_1fr_48px] items-center text-[14px] ${
												index !==
												filteredRows.length - 1
													? 'border-b border-[#ECEFF3]'
													: ''
											}`}>
											<div className='px-4 py-4 text-[#17395f] font-medium text-left'>
												{row.setorNome}
											</div>
											<div className='px-4 py-4 text-[#5a5f66] text-center'>
												{formatAndarEscala(
													row.andar,
													rows.map(
														(item) => item.andar,
													),
												)}
											</div>
											<div className='px-4 py-4 text-[#5a5f66] text-center'>
												{row.tipoQuantidade}
											</div>
											<div className='px-4 py-4 text-[#5a5f66] text-center'>
												{String(row.quantidade || 0)}
											</div>
											<div className='px-4 py-4 flex justify-center'>
												<button
													type='button'
													onClick={() =>
														abrirPopupExclusao(row)
													}
													disabled={
														operacoesBloqueadas
													}
													className='text-[#E35D5B] hover:text-[#c74947] disabled:opacity-50 disabled:cursor-not-allowed'>
													<FiTrash2 size={16} />
												</button>
											</div>
										</div>
									))
								)}
							</div>
						</div>
					</div>
				</main>

				{deleteTarget && (
					<div className='fixed inset-0 z-[60] flex items-center justify-center bg-black/30 px-4'>
						<div className='w-full max-w-[516px] rounded-lg border border-[#d9d9d9] bg-[#f5f5f5] shadow-xl'>
							<div className='p-5'>
								<div className='mb-5 flex items-center gap-3'>
									<div className='flex h-12 w-12 items-center justify-center rounded-full bg-[#e2e2e2] text-[#b03f3f]'>
										<FiAlertTriangle size={24} />
									</div>
									<h3 className='text-[20px] font-semibold font-inter text-[#4f5257]'>
										Confirmar Exclusão
									</h3>
								</div>

								<p className='mb-2 text-[16px] font-normal text-start text-[#595d63]'>
									Você tem certeza que deseja excluir{' '}
									{getDeleteTargetLabel(deleteTarget.tipo)}?
								</p>
								<p className='text-[16px] font-normal text-start text-[#595d63]'>
									Esta ação não pode ser desfeita.
								</p>

								<div className='mt-8 flex justify-center gap-4'>
									<button
										type='button'
										onClick={fecharPopupExclusao}
										disabled={isDeletingItem}
										className='min-w-[100px] rounded-lg border border-[#d7d7d7] bg-[#f6f6f6] px-5 py-2 text-[14px] font-medium text-[#60656d] hover:bg-[#ececec] disabled:cursor-not-allowed disabled:opacity-70'>
										Cancelar
									</button>
									<button
										type='button'
										onClick={confirmarExclusaoItem}
										disabled={isDeletingItem}
										className='min-w-[100px] rounded-lg border border-[#b14a46] bg-[#bc514d] px-5 py-2 text-[14px] font-semibold text-white hover:bg-[#aa4743] disabled:cursor-not-allowed disabled:opacity-70'>
										{isDeletingItem
											? 'Excluindo...'
											: 'Excluir'}
									</button>
								</div>
							</div>
						</div>
					</div>
				)}
			</div>
		</div>
	);
}

export default ScaleManager;
